import { AdminLayout } from '@/components/layouts'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Link, router } from '@inertiajs/react'
import {
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  Eye,
  Edit,
  Trash2,
  BarChart3,
  TrendingUp,
  Users,
  CheckCircle2
} from 'lucide-react'
import { useState, useCallback } from 'react'

interface AdminSurvey {
  id: number
  title: string
  date_range: string
  community_name: string | null
  published: boolean
  active: boolean
  total_responses: number
  answers_count: number
  multiple_choice: boolean
  created_at: string
}

interface Pagination {
  current_page: number
  per_page: number
  total_count: number
  total_pages: number
}

interface Filters {
  search: string | null
  community_id: string | null
  published: string | null
  period: string | null
  sort: string | null
  direction: string | null
}

interface Stats {
  total: number
  published: number
  active: number
  total_responses: number
}

interface Community {
  id: number
  name: string
}

interface IndexProps {
  surveys: AdminSurvey[]
  pagination: Pagination
  filters: Filters
  stats: Stats
  communities: Community[]
}

export default function Index({ surveys, pagination, filters, stats, communities }: IndexProps) {
  const [search, setSearch] = useState(filters.search || '')

  const applyFilters = useCallback((newFilters: Partial<Filters>) => {
    router.get('/admin/surveys', {
      ...filters,
      ...newFilters,
      page: 1
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }, [filters])

  const handleSearch = useCallback(() => {
    applyFilters({ search: search || null })
  }, [search, applyFilters])

  const handleSort = useCallback((field: string) => {
    const newDirection = filters.sort === field && filters.direction === 'desc' ? 'asc' : 'desc'
    applyFilters({ sort: field, direction: newDirection })
  }, [filters, applyFilters])

  const handlePageChange = useCallback((page: number) => {
    router.get('/admin/surveys', {
      ...filters,
      page
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }, [filters])

  const handleDelete = useCallback((survey: AdminSurvey) => {
    if (confirm(`Voulez-vous vraiment supprimer ce sondage ?\n"${survey.title}"`)) {
      router.delete(`/admin/surveys/${survey.id}`)
    }
  }, [])

  return (
    <AdminLayout
      title="Sondages"
      breadcrumbs={[{ label: 'Sondages' }]}
    >
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-gray mb-1">Total Sondages</p>
              <p className="text-2xl font-bold text-text-dark">{stats.total}</p>
            </div>
            <BarChart3 className="text-gray-300" size={32} />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-gray mb-1">Publiés</p>
              <p className="text-2xl font-bold text-tessan-primary">{stats.published}</p>
            </div>
            <CheckCircle2 className="text-tessan-primary/30" size={32} />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-gray mb-1">Actifs</p>
              <p className="text-2xl font-bold text-green-600">{stats.active}</p>
            </div>
            <TrendingUp className="text-green-300" size={32} />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-gray mb-1">Total Réponses</p>
              <p className="text-2xl font-bold text-text-dark">{stats.total_responses}</p>
            </div>
            <Users className="text-gray-300" size={32} />
          </div>
        </Card>
      </div>

      {/* Actions Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4 mb-6">
        <div className="flex flex-1 gap-4">
          <div className="flex-1 max-w-md">
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="Rechercher dans les sondages..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch} variant="secondary">
                <Search size={18} />
              </Button>
            </div>
          </div>
        </div>

        <Link href="/admin/surveys/new">
          <Button className="flex items-center gap-2">
            <Plus size={18} />
            Nouveau Sondage
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="w-48">
            <Select
              value={filters.community_id || ''}
              onChange={(e) => applyFilters({ community_id: e.target.value || null })}
            >
              <option value="">Toutes communautés</option>
              {communities.map((community) => (
                <option key={community.id} value={community.id}>
                  {community.name}
                </option>
              ))}
            </Select>
          </div>

          <div className="w-48">
            <Select
              value={filters.published || ''}
              onChange={(e) => applyFilters({ published: e.target.value || null })}
            >
              <option value="">Tous statuts</option>
              <option value="true">Publiés</option>
              <option value="false">Brouillons</option>
            </Select>
          </div>

          <div className="w-48">
            <Select
              value={filters.period || ''}
              onChange={(e) => applyFilters({ period: e.target.value || null })}
            >
              <option value="">Toutes périodes</option>
              <option value="active">Actifs</option>
              <option value="past">Terminés</option>
              <option value="upcoming">À venir</option>
            </Select>
          </div>

          {(filters.search || filters.community_id || filters.published || filters.period) && (
            <Button
              variant="secondary"
              onClick={() => {
                setSearch('')
                applyFilters({ search: null, community_id: null, published: null, period: null })
              }}
            >
              Réinitialiser
            </Button>
          )}
        </div>
      </Card>

      {/* Results Info */}
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-text-gray">
          {pagination.total_count} sondage{pagination.total_count !== 1 ? 's' : ''}
          {pagination.total_pages > 1 && (
            <span> - Page {pagination.current_page} sur {pagination.total_pages}</span>
          )}
        </p>
      </div>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3">
                  <button
                    className="flex items-center gap-1 font-medium text-text-gray hover:text-text-dark"
                    onClick={() => handleSort('title')}
                  >
                    Titre
                    <ArrowUpDown size={14} />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">Période</th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">Communauté</th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">Type</th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">Statut</th>
                <th className="text-left px-4 py-3">
                  <button
                    className="flex items-center gap-1 font-medium text-text-gray hover:text-text-dark"
                    onClick={() => handleSort('responses')}
                  >
                    Réponses
                    <ArrowUpDown size={14} />
                  </button>
                </th>
                <th className="text-right px-4 py-3 font-medium text-text-gray">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {surveys.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-text-gray">
                    <div className="flex flex-col items-center gap-2">
                      <BarChart3 size={32} className="text-gray-300" />
                      Aucun sondage trouvé
                    </div>
                  </td>
                </tr>
              ) : (
                surveys.map((survey) => (
                  <tr key={survey.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-text-dark">{survey.title}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-text-dark whitespace-nowrap">{survey.date_range}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-text-dark">{survey.community_name || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${survey.multiple_choice ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                        {survey.multiple_choice ? 'Choix multiples' : 'Choix unique'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {survey.published ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Publié</span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">Brouillon</span>
                        )}
                        {survey.active && (
                          <span className="inline-flex w-2 h-2 bg-green-500 rounded-full" title="Actif" />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-medium text-text-dark">{survey.total_responses}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Link href={`/admin/surveys/${survey.id}`}>
                          <Button variant="secondary" className="p-2"><Eye size={16} /></Button>
                        </Link>
                        <Link href={`/admin/surveys/${survey.id}/edit`}>
                          <Button variant="secondary" className="p-2"><Edit size={16} /></Button>
                        </Link>
                        <Button
                          variant="secondary"
                          className="p-2 text-red-600 hover:bg-red-50"
                          onClick={() => handleDelete(survey)}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <Button
              variant="secondary"
              disabled={pagination.current_page === 1}
              onClick={() => handlePageChange(pagination.current_page - 1)}
              className="flex items-center gap-1"
            >
              <ChevronLeft size={16} /> Précédent
            </Button>
            <div className="flex items-center gap-2">
              {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                let pageNum: number
                if (pagination.total_pages <= 5) {
                  pageNum = i + 1
                } else if (pagination.current_page <= 3) {
                  pageNum = i + 1
                } else if (pagination.current_page >= pagination.total_pages - 2) {
                  pageNum = pagination.total_pages - 4 + i
                } else {
                  pageNum = pagination.current_page - 2 + i
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`w-8 h-8 rounded text-sm font-medium transition-colors ${
                      pagination.current_page === pageNum
                        ? 'bg-tessan-primary text-white'
                        : 'bg-white text-text-gray hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              })}
            </div>
            <Button
              variant="secondary"
              disabled={pagination.current_page === pagination.total_pages}
              onClick={() => handlePageChange(pagination.current_page + 1)}
              className="flex items-center gap-1"
            >
              Suivant <ChevronRight size={16} />
            </Button>
          </div>
        )}
      </Card>
    </AdminLayout>
  )
}
